/**
 * توابع کمکی فرمت‌بندی اعداد، تاریخ و رشته‌ها
 */

/** یک تومان = ۱۰ ریال — API/DB همچنان ریال است؛ UI با تومان کار می‌کند. */
export const IRR_PER_TOMAN = 10;

/** تبدیل مبلغ ذخیره‌شده (ریال) به تومان برای نمایش/فرم */
export const irrToToman = (irr) => {
  if (irr === null || irr === undefined || irr === '') return null;
  const num = Number(irr);
  if (Number.isNaN(num)) return null;
  return Math.round(num / IRR_PER_TOMAN);
};

/** تبدیل ورودی تومان کاربر به ریال برای ارسال به API */
export const tomanToIrr = (toman) => {
  if (toman === null || toman === undefined || toman === '') return null;
  const num = Number(toman);
  if (Number.isNaN(num)) return null;
  return Math.round(num * IRR_PER_TOMAN);
};

/**
 * فرمت‌بندی اعداد با جداکننده هزارگان (فارسی)
 * @param {number} value
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  if (value === '—' || value === '-') return value;
  if (typeof value === 'string' && value.trim() === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) {
    if (typeof value === 'string') return value;
    return '—';
  }
  return num.toLocaleString('fa-IR');
};

/**
 * مبلغ با واحد پول.
 * برای IRR/IRT/TOMAN مقدار ورودی از API به ریال فرض می‌شود و به تومان نمایش داده می‌شود.
 * برای USD اگر نرخ fxUsdIrr (ریال به ازای هر دلار) داده شود، معادل تومان نمایش داده می‌شود.
 */
export const formatMoney = (value, currency = 'IRR', { fxUsdIrr } = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const code = String(currency || 'IRR').toUpperCase();
  if (code === 'IRR' || code === 'IRT' || code === 'TOMAN' || code === 'TMN') {
    const toman = irrToToman(value);
    return `${formatNumber(toman)} تومان`;
  }
  if (code === 'USD' && fxUsdIrr != null && !Number.isNaN(Number(fxUsdIrr))) {
    return formatMoney(Number(value) * Number(fxUsdIrr), 'IRR');
  }
  // بدون نرخ تبدیل، مبلغ‌های غیرتومانی را هم با برچسب تومان نشان نده؛
  // برای UI کاربر همیشه تومان — caller باید fxUsdIrr بدهد یا از قبل تبدیل کند.
  return `${formatNumber(value)} تومان`;
};

/** تبدیل هزینه تخمینی دلار به نمایش تومان با نرخ FX (ریال/دلار). */
export const formatUsdCostAsToman = (usd, fxUsdIrr) => {
  if (usd === null || usd === undefined || Number.isNaN(Number(usd))) return '—';
  if (fxUsdIrr == null || Number.isNaN(Number(fxUsdIrr)) || Number(fxUsdIrr) <= 0) {
    return '—';
  }
  return formatMoney(Number(usd) * Number(fxUsdIrr), 'IRR');
};

/**
 * برآورد AI سازمان: تومان. اگر کیف‌پول معادل ریال داده باشد همان مبنای کسر اعتبار است.
 */
export const formatQuoteToman = (usd, wallet) => {
  if (usd === null || usd === undefined || Number.isNaN(Number(usd))) return '—';
  const amountUsd = Number(usd);
  if (amountUsd === 0) return formatMoney(0, 'IRR');
  const quotedUsd = Number(wallet?.usd_cost);
  const irrEq = Number(wallet?.irr_equivalent);
  if (Number.isFinite(quotedUsd) && quotedUsd > 0 && Number.isFinite(irrEq) && irrEq > 0) {
    return formatMoney((amountUsd / quotedUsd) * irrEq, 'IRR');
  }
  return formatUsdCostAsToman(amountUsd, wallet?.fx_usd_irr);
};

/**
 * فرمت‌بندی کوتاه اعداد با واحد فارسی.
 */
export const formatCompact = (value) => {
  if (value === null || value === undefined) return '-';
  const num = Number(value);
  if (Math.abs(num) >= 1_000_000) return `${formatNumber((num / 1_000_000).toFixed(1))} میلیون`;
  if (Math.abs(num) >= 1_000) return `${formatNumber((num / 1_000).toFixed(1))} هزار`;
  return formatNumber(num);
};

/**
 * مبلغ تومان برای کارت‌های فشرده داشبورد: عدد جدا از واحد تا در باکس جا شود.
 * ورودی مثل formatMoney به ریال است.
 * @returns {{ value: string, unit: string, title: string }}
 */
export const formatCompactTomanParts = (irrAmount) => {
  const toman = irrToToman(irrAmount);
  const title = formatMoney(irrAmount, 'IRR');
  if (toman === null) return { value: '—', unit: 'تومان', title };
  const abs = Math.abs(toman);
  if (abs >= 1_000_000_000) {
    return { value: formatNumber((toman / 1_000_000_000).toFixed(1)), unit: 'میلیارد تومان', title };
  }
  if (abs >= 1_000_000) {
    return { value: formatNumber((toman / 1_000_000).toFixed(1)), unit: 'میلیون تومان', title };
  }
  return { value: formatNumber(toman), unit: 'تومان', title };
};

/**
 * تبدیل اعداد انگلیسی به فارسی
 */
export const toPersianDigits = (value) => {
  if (value === null || value === undefined) return '';
  const map = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(value).replace(/\d/g, (d) => map[d]);
};

/**
 * مدت زمان به شکل ساعت‌شمار: ۰۰:۵۵:۲۴ یا ۱ روز و ۲۲:۱۰:۰۲
 * @param {number|null|undefined} seconds
 */
export const formatDurationClock = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '—';
  const total = Math.max(0, Math.round(Number(seconds)));
  const days = Math.floor(total / 86400);
  const rem = total % 86400;
  const h = Math.floor(rem / 3600);
  const m = Math.floor((rem % 3600) / 60);
  const s = rem % 60;
  const clock = [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  if (days > 0) {
    return `${toPersianDigits(days)} روز و ${toPersianDigits(clock)}`;
  }
  return toPersianDigits(clock);
};

/**
 * فرمت‌بندی درصد با علامت
 */
export const formatPercent = (value, fractionDigits = 1) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const sign = value > 0 ? '+' : '';
  return `${toPersianDigits(`${sign}${Number(value).toFixed(fractionDigits)}`)}٪`;
};

/**
 * گرفتن حرف اول از نام برای آواتار
 */
export const getInitials = (name = '') => {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};
